import { useRef, useState } from 'react';
import {
  createCompletedState,
  createErrorState,
  createIdleState,
  createProcessingState,
  createReadyState,
} from '../types/pageState';

import { DEMO_MENU_ITEMS } from '../data/demoMenuItems';
import { ImageCompressionService } from '../services/ImageCompressionService';
import {
  beginScanSession,
  buildAnalyticsHeaders,
  captureProductEvent,
  updateProductAnalyticsContext,
} from '../utils/productAnalytics';

export const useMenuProcessing = () => {
  // Image state
  const [selectedImage, setSelectedImage] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [imageCompressing, setImageCompressing] = useState(false);

  // Single page state replaces all boolean flags
  const [pageState, setPageState] = useState(createIdleState());

  // Refs
  const responseSectionRef = useRef(null);
  const selectedSourceRef = useRef('unknown');

  // Set default preview when no image selected
  const setDefaultPreview = () => {
    if (!selectedImage && !previewUrl) {
      setPreviewUrl('/mejicana.jpeg');
      // Only reset to idle if we're not currently processing or in a demo
      setPageState(prevState => {
        if (prevState.status === 'processing' || prevState.status === 'complete') {
          // Don't reset if we're in the middle of processing or completed
          return prevState;
        }
        return createIdleState();
      });
    }
  };

  const getImageAnalyticsProperties = (file, extra = {}) => ({
    imageMimeType: file?.type,
    imageSize: file?.size,
    fileType: file?.type || file?.name?.split('.').pop()?.toLowerCase(),
    fileSizeBucket:
      !file?.size ? undefined : file.size < 1_000_000 ? 'under_1mb' : file.size < 5_000_000 ? '1mb_to_5mb' : 'over_5mb',
    imageNameLength: file?.name?.length,
    ...extra,
  });

  const captureProcessingFailure = (stage, error, extra = {}) => {
    const errorMessage = error?.message || String(error || 'Unknown error');

    captureProductEvent('scan-failed', {
      stage,
      failureStage: stage,
      failureCode: extra.failureCode || `scan_${stage}`,
      errorMessage,
      paywallReason: errorMessage.toLowerCase().includes('credits') ? 'out_of_credits' : undefined,
      ...extra,
    });

    if (errorMessage.toLowerCase().includes('credits')) {
      captureProductEvent('paywall-viewed', {
        trigger: 'scan_blocked',
        reason: 'out_of_credits',
        stage,
      });
    }
  };

  const handleImageSelect = async (event, source = 'unknown') => {
    const file = event.target.files[0];
    if (file) {
      selectedSourceRef.current = source;
      updateProductAnalyticsContext({ source });
      const isHEIC =
        file.type.toLowerCase().includes('heic') ||
        file.type.toLowerCase().includes('heif') ||
        file.name.toLowerCase().endsWith('.heic') ||
        file.name.toLowerCase().endsWith('.heif');

      captureProductEvent('upload-started', getImageAnalyticsProperties(file, { source, isHEIC }));

      if (isHEIC) {
        captureProductEvent(
          'upload-failed',
          getImageAnalyticsProperties(file, {
            source,
            reason: 'unsupported_heic',
          })
        );
        captureProductEvent('upload-rejected', {
          ...getImageAnalyticsProperties(file, { source }),
          failureStage: 'validation',
          failureCode: 'unsupported_heic',
        });
        setPageState(
          createErrorState(
            'HEIC/Live Photo Format Not Supported',
            'We cannot process HEIC/HEIF images (Live Photos). Please convert your image to JPG or PNG format before uploading, or take a regular photo instead of a Live Photo.'
          )
        );
        event.target.value = '';
        return;
      }

      setImageCompressing(true);
      try {
        console.log('[IMAGE] Compressing uploaded image...');
        const compressionResult = await ImageCompressionService.compressImage(file);

        setSelectedImage(compressionResult.file);
        setPreviewUrl(URL.createObjectURL(compressionResult.file));
        setPageState(createReadyState());
        captureProductEvent(
          'upload-ready',
          getImageAnalyticsProperties(compressionResult.file, {
            source,
            originalImageSize: file.size,
            compressed: true,
          })
        );
        captureProductEvent('upload-compression-completed', {
          ...getImageAnalyticsProperties(compressionResult.file, { source }),
          originalImageSize: file.size,
        });
      } catch (error) {
        console.error('Image compression failed:', error);
        captureProductEvent(
          'upload-compression-failed',
          getImageAnalyticsProperties(file, {
            source,
            errorMessage: error.message,
            failureStage: 'compression',
            failureCode: 'image_compression_failed',
          })
        );
        setSelectedImage(file);
        setPreviewUrl(URL.createObjectURL(file));
        setPageState(createReadyState());
        captureProductEvent('upload-ready', getImageAnalyticsProperties(file, { source, compressed: false }));
      } finally {
        setImageCompressing(false);
      }
    }
  };

  const handleCameraCapture = compressedFile => {
    selectedSourceRef.current = 'camera';
    updateProductAnalyticsContext({ source: 'camera' });
    setImageCompressing(true);
    try {
      setSelectedImage(compressedFile);
      setPreviewUrl(URL.createObjectURL(compressedFile));
      setPageState(createReadyState());
      captureProductEvent(
        'upload-ready',
        getImageAnalyticsProperties(compressedFile, {
          source: 'camera_capture',
          compressed: true,
        })
      );
    } finally {
      setImageCompressing(false);
    }
  };

  // handleUploadFromGallery is now handled directly by UploadSection component

  const handleClearImage = () => {
    if (pageState.status === 'processing') {
      captureProductEvent('scan-cancelled', { sessionId: pageState.sessionId });
    }
    setSelectedImage(null);
    setPreviewUrl('');
    setPageState(createIdleState());
  };

  const startDemo = async (source = 'unknown') => {
    return new Promise(resolve => {
      const fullDemoItems = DEMO_MENU_ITEMS.map(item => ({ ...item }));

      beginScanSession({ source: 'demo', entryPoint: source });
      captureProductEvent('demo-menu-started', { source: 'demo', entryPoint: source });
      captureProductEvent('scan-started', { source: 'demo', entryPoint: source, isDemo: true });

      const demoStages = [
        { progress: 20, message: 'Processing demo menu...', delay: 500 },
        { progress: 60, message: 'Loading all menu items...', delay: 800, showItems: true },
        { progress: 100, message: 'All menu items loaded successfully!', delay: 500, complete: true },
      ];

      let currentStage = 0;

      // Start processing with empty items
      setPageState(createProcessingState({ items: [], sessionId: 'demo-session' }));

      const processNextStage = () => {
        if (currentStage >= demoStages.length) return;

        const stage = demoStages[currentStage];

        if (stage.showItems) {
          // Update with demo items
          setPageState(createProcessingState({ items: fullDemoItems, sessionId: 'demo-session' }));
          captureProductEvent('first-results-received', {
            sessionId: 'demo-session',
            itemCount: fullDemoItems.length,
            source: 'demo',
            entryPoint: source,
            isDemo: true,
          });
        } else if (stage.complete) {
          // Final success state
          setPageState(createCompletedState(fullDemoItems, 'Ow-kp4zUUZo'));
          captureProductEvent('scan-succeeded', {
            sessionId: 'demo-session',
            itemCount: fullDemoItems.length,
            source: 'demo',
            entryPoint: source,
            isDemo: true,
          });
          captureProductEvent('results-rendered', {
            sessionId: 'demo-session',
            itemCount: fullDemoItems.length,
            source: 'demo',
            entryPoint: source,
            isDemo: true,
          });
          resolve({ items: fullDemoItems, sessionId: 'Ow-kp4zUUZo' });
        } else {
          // Keep current items, just simulate processing
          setPageState(createProcessingState({ items: [], sessionId: 'demo-session' }));
        }

        if (!stage.complete) {
          currentStage++;
          setTimeout(processNextStage, stage.delay);
        }
      };

      processNextStage();
    });
  };

  const startProcessing = file => {
    const source = selectedSourceRef.current;
    const processingStartedAt = Date.now();
    setPageState(createProcessingState({ items: [] }));
    captureProductEvent('scan-started', getImageAnalyticsProperties(file, { source }));
    const eventSourceRef = { current: null };
    let currentItems = []; // Track items across SSE events
    let retryCount = 0;
    let capturedFirstResults = false;
    const baseDelayMs = 3000; // 3s, then 6s, then 12s

    const connect = async () => {
      const formData = new FormData();
      formData.append('image', file);

      try {
        const uploadRes = await fetch('/start-menu-processing', {
          method: 'POST',
          body: formData,
          credentials: 'include',
          headers: buildAnalyticsHeaders(),
        });

        if (!uploadRes.ok) {
          const errorData = await uploadRes.json();
          console.log('errorData', errorData);
          throw new Error(errorData.message || 'Failed to start processing');
        }
        const { sessionId } = await uploadRes.json();

        captureProductEvent('scan-session-created', { sessionId, source });
        setPageState(createProcessingState({ items: [], sessionId }));
        const createSSEConnectionWithRetries = async () => {
          const eventSource = new EventSource(`/menu-processing-stream/${sessionId}`, {
            withCredentials: true,
          });
          eventSourceRef.current = eventSource;
          eventSource.onmessage = event => {
            try {
              const data = JSON.parse(event.data);

              switch (data.status) {
                case 'processing':
                  if (data.menuItems && Array.isArray(data.menuItems)) {
                    currentItems = data.menuItems;
                    if (!capturedFirstResults && currentItems.length > 0) {
                      capturedFirstResults = true;
                      captureProductEvent('first-results-received', {
                        sessionId,
                        itemCount: currentItems.length,
                        source,
                      });
                    }
                    setPageState(
                      createProcessingState({
                        items: currentItems,
                        sessionId,
                      })
                    );
                  }
                  break;

                case 'complete':
                  const finalItems = data.menuItems || currentItems;
                  setPageState(createCompletedState(finalItems, sessionId));
                  captureProductEvent('scan-succeeded', {
                    sessionId,
                    itemCount: finalItems.length,
                    source,
                    processingDurationMs: Date.now() - processingStartedAt,
                  });
                  captureProductEvent(finalItems.length ? 'results-rendered' : 'result-empty', {
                    sessionId,
                    itemCount: finalItems.length,
                    source,
                    processingDurationMs: Date.now() - processingStartedAt,
                  });
                  eventSource.close();
                  eventSourceRef.current = null;
                  break;

                case 'error':
                  throw new Error(data.message || 'Processing failed');

                default:
                  console.warn('Unknown SSE status:', data.status);
                  break;
              }
            } catch (parseError) {
              console.error('Error parsing streaming event:', parseError);
              captureProcessingFailure('stream_parse', parseError, { sessionId });
              setPageState(
                createErrorState(
                  parseError.message || 'Failed to parse server response',
                  parseError.message || 'An unexpected error occurred during processing.'
                )
              );
              eventSource.close();
              eventSourceRef.current = null;
            }
          };
          eventSource.onerror = error => {
            eventSource.close();
            eventSourceRef.current = null;
            console.error('EventSource error:', error);
            if (retryCount < 3) {
              const delay = baseDelayMs * Math.pow(2, retryCount);
              retryCount++;
              captureProductEvent('scan-retried', {
                sessionId,
                retryCount,
                delay,
                source,
              });
              setTimeout(() => {
                createSSEConnectionWithRetries();
              }, delay);
            } else {
              captureProcessingFailure('stream_connection', new Error('Lost connection to server'), { sessionId });
              setPageState(createErrorState('Connection error', 'Lost connection to server. Please try again.'));
            }
          };
        };

        createSSEConnectionWithRetries();
      } catch (error) {
        console.error('Error with streaming conversion:', error);
        captureProcessingFailure('upload_start', error);
        setPageState(
          createErrorState(
            error.message || 'Failed to process image',
            error.message || 'An unexpected error occurred. Please try again with a different image.'
          )
        );
      }
    };

    const cancel = () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      captureProductEvent('scan-cancelled', { sessionId: pageState.sessionId, source });
      setPageState(createReadyState());
    };

    return { connect, cancel };
  };

  return {
    // Image state
    selectedImage,
    previewUrl,
    imageCompressing,

    // Single page state (replaces all boolean flags)
    pageState,

    // Refs
    responseSectionRef,

    // Actions
    setDefaultPreview,
    handleImageSelect,
    handleCameraCapture,
    handleClearImage,
    startDemo,
    startProcessing,

    // Direct state control (for complex interactions)
    setPageState,
  };
};
